import { useRouter } from 'next/router';

import { useShortIdContext } from '@air/provider-short-id';

import {
  findShowcaseSubPageBySlug,
  getShowcaseSubPageSlugFromPath,
  type ShowcaseSubPage,
} from '~/components/v2/components/showcase/showcaseSubPageRoutes';

// Module scope so an un-authored link doesn't hand a fresh array to the resolver on every render.
const EMPTY_SUB_PAGES: ShowcaseSubPage[] = [];

/**
 * The collection sub-page the viewer is currently on, or null on the link's root page.
 *
 * Reads `asPath` (the `useURLClipIdSelector` idiom) rather than `useRouterSelector`'s
 * `window.location` on purpose: this value decides what the public page RENDERS, and
 * `useRouterSelector` has no value during SSR, which would hydrate a sub-page URL as the root page.
 *
 * Returns null everywhere a sub-page URL can't exist — the share link's root/board/clip routes and
 * the authenticated editor canvas — so every non-sub-page surface keeps today's behavior.
 *
 * URL-only on EVERY surface: the editor's Preview iframe carries the same `/page/{slug}` segment on
 * its own preview routes, so the path names the previewed page there exactly as it does on the live
 * page (the slug matcher is base-path agnostic). There is deliberately no canvas branch here: the
 * inline canvas overrides the body and gallery scope through its own channels and relies on this
 * hook staying URL-null (the editor route has no `/page/` segment).
 */
export const usePublicShowcaseActiveSubPage = () => {
  const { asPath } = useRouter();
  const { presentationPages } = useShortIdContext();
  const subPages = presentationPages?.subPages ?? EMPTY_SUB_PAGES;

  return findShowcaseSubPageBySlug({
    slug: getShowcaseSubPageSlugFromPath(asPath),
    subPages,
  });
};
